import { Axios } from 'api/axios';
import { useFormik } from "formik";
import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from "yup";

const url = process.env.REACT_APP_BASE_URL;

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  
  const [cooldown, setCooldown] = useState(300);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (!email) {
      toast.error("Please request OTP first");
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    
    const timer = setInterval(() => {
      setCooldown(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [cooldown]);

  const validation = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("Please Enter OTP")
        .matches(/^\d{6}$/, "OTP must be exactly 6 digits"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          email: email,
          otp: values.otp,
        };

        const res: any = await Axios.post(`api/user/verify-otp/`, payload);

        if (res?.message || res?.data?.message) {
          toast.success("OTP verified successfully!");
          // Navigate to reset password page
          navigate('/reset-password', { state: { email: email, otp: values.otp } });
        } else {
          toast.error("OTP verification failed");
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Invalid OTP");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleResendOTP = async () => {
    if (cooldown > 0) {
      toast.warning(`Please wait ${Math.floor(cooldown / 60)}m ${cooldown % 60}s before requesting a new OTP.`);
      return;
    }

    setIsResending(true);
    try {
      const res: any = await Axios.post(`api/user/forgot-password-request/`, {
        email: email
      });

      if (res?.message) {
        toast.success("OTP resent successfully!");
        setCooldown(300);
      } else {
        toast.error("Failed to resend OTP");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="col-xxl-6 mx-auto">
      <Card className="card mb-0 border-0 shadow-none mb-0">
        <Card.Body className="p-sm-5 m-lg-4">
          <div className="text-center mt-2">
            <h5 className="fs-3xl">Verify OTP</h5>
            <p className="text-muted mb-4">Enter the OTP sent to your email</p>
          </div>
          
          <Alert className="border-0 alert-info text-center mb-2 mx-2" role="alert">
            OTP sent to: <strong>{email}</strong>
          </Alert>
          
          <div className="p-2">
            <Form onSubmit={validation.handleSubmit}>
              <div className="mb-3">
                <Form.Label className="form-label">Enter OTP</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit OTP"
                  name="otp"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.otp}
                  isInvalid={validation.touched.otp && !!validation.errors.otp}
                  className="text-center fs-5 letter-spacing-3"
                />
                <Form.Control.Feedback type="invalid">
                  {validation.errors.otp}
                </Form.Control.Feedback>
                {cooldown > 0 && (
                  <Form.Text className="text-muted d-block mt-2 text-center">
                    OTP expires in <strong>{formatTime(cooldown)}</strong>
                  </Form.Text>
                )}
              </div>

              <div className="text-center mt-4">
                <Button className="btn btn-primary w-100" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Verifying...
                    </>
                  ) : (
                    "Verify OTP"
                  )}
                </Button>
              </div>

              <div className="mt-3 text-center">
                <Button 
                  variant="link" 
                  className="text-decoration-none"
                  onClick={handleResendOTP}
                  disabled={cooldown > 0 || isResending}
                >
                  {isResending ? "Resending..." : cooldown > 0 ? `Resend OTP (${formatTime(cooldown)})` : "Resend OTP"}
                </Button>
              </div>

              <div className="mt-2 text-center">
                <Link to="/forgot-password" className="text-muted">
                  ← Back to forgot password
                </Link>
              </div>
            </Form>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default VerifyOTP;

